import { makeAutoObservable, reaction } from "mobx";
import type { MapRef } from "react-map-gl/mapbox";
import type { RefObject } from "react";
import { RainLayers } from "@/components/Map/Rain/RainLayers";
import { RadarTimelineStore } from "@/components/Map/Rain/RadarTimelineStore";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { rain } from "@/endpoints/rain/rain";

/**
 * Self-contained store for rain gauges + rain radar overlays on a single map.
 * Composes RainLayers (mapbox layer wiring) + RadarTimelineStore (radar scrubbing).
 *
 * Lifecycle: construct, attach() once the map has loaded, detach() on unmount.
 *
 * Skipped (compared to the original page map): rain popup, gauge connection
 * lines, and the structure->nearest-gauge highlight. The underlying layers
 * remain in RainLayers but are never shown by this store.
 */
export class RainStore {
	showRainGauges = false;
	showRainRadar = false;
	rainLayers: RainLayers;
	radarTimeline = new RadarTimelineStore();
	// Wrapped in AsyncStateStore so the fetch has its own scoped abort signal
	// instead of inheriting a sibling store's signal via Api.currentSignal,
	// which previously caused the request to be cancelled mid-toggle.
	stationsApi = new AsyncStateStore(rain.getAllStations);

	private radarFetched = false;
	private cachedGaugeData: GeoJSON.FeatureCollection | null = null;
	private radarUpdateId = 0;
	private timelineReactionDisposer?: () => void;

	constructor(mapRef: RefObject<MapRef | null>, belowLayerId?: string) {
		this.rainLayers = new RainLayers(mapRef, belowLayerId);

		makeAutoObservable(this, {
			rainLayers: false,
			radarUpdateId: false,
		} as any);

		// When the user scrubs the radar timeline, swap the displayed radar image.
		this.timelineReactionDisposer = reaction(
			() => this.radarTimeline.selectedIndex,
			async () => {
				const image = this.radarTimeline.selectedImage;
				if (!image || !this.showRainRadar) return;
				const updateId = ++this.radarUpdateId;
				const url = image.url || (await this.radarTimeline.getImageUrl(image.id));
				if (!url || updateId !== this.radarUpdateId) return;
				this.rainLayers.updateRadarImage(url, [
					[image.minLon, image.maxLat],
					[image.maxLon, image.maxLat],
					[image.maxLon, image.minLat],
					[image.minLon, image.minLat],
				]);
			},
		);
	}

	attach = () => {
		this.rainLayers.attach();
	};

	detach = () => {
		this.timelineReactionDisposer?.();
		this.timelineReactionDisposer = undefined;
		this.rainLayers.detach();
	};

	toggleRainGauges = async () => {
		this.showRainGauges = !this.showRainGauges;
		if (this.showRainGauges) {
			if (!this.cachedGaugeData) {
				const response = await this.stationsApi.fetchIfEmpty();
				this.cachedGaugeData = this.toFeatureCollection(response);
			}
			this.rainLayers.setGaugeData(this.cachedGaugeData);
		}
		this.rainLayers.setGaugesVisible(this.showRainGauges);
	};

	toggleRainRadar = async () => {
		this.showRainRadar = !this.showRainRadar;
		if (this.showRainRadar && !this.radarFetched) {
			this.radarFetched = true;
			await Promise.all([this.rainLayers.loadRadarImage(), this.radarTimeline.loadTimeline()]);
		}
		if (!this.showRainRadar) {
			this.radarTimeline.pause();
		}
		this.rainLayers.setRadarVisible(this.showRainRadar);
	};

	private toFeatureCollection = (
		response: Awaited<ReturnType<typeof rain.getAllStations>>,
	): GeoJSON.FeatureCollection => ({
		type: "FeatureCollection",
		features: response.stations.map(station => ({
			type: "Feature" as const,
			properties: {
				id: station.id,
				name: station.name,
				provider: station.provider,
				elevation: station.elevation,
			},
			geometry: {
				type: "Point" as const,
				coordinates: [station.lon, station.lat],
			},
		})),
	});
}
